/**
 * ShapeSearchManager - Level 3 Manager
 *
 * RESPONSIBILITY:
 * Manages the complete shape search strategy including both first-fit and best-fit phases.
 *
 * HIERARCHY:
 * - Reports to: ConfigurationProcessor (Level 2)
 * - Manages: Shape selection and search strategies
 * - Peers: CandidateProcessor, ConfigurationOptimizer
 *
 * KEY OPERATIONS:
 * - findInitialShapes: First-fit logic for 3 initial shapes
 * - refineToBestShape: Best-fit iterative refinement
 * - executeCompleteSearch: Combined first-fit + best-fit flow
 *
 * NAVIGATION:
 * - For processing logic, see: ./candidate-processor.ts
 * - For optimization, see: ./optimizers/configuration-optimizer.ts
 * - For configuration coordination, see: ../configuration-processor.ts
 */

import { buildFirstFitCandidates } from '~/builders/candidate-builder';
import type { SimplifiedProjectData } from '~/engine/millage/shape-generators/general-processing';
import { logger } from '~/services/logging';
import type { Candidate, Configuration, Project } from '~/types';
import type { BaseProjectElements } from '~/types/candidate';
import { MaxValueStrategy } from './strategies/max-value-strategy';
import { type BestFitStrategy, ProjectType, type SearchContext } from './strategies/strategy-types';
import { TargetValueStrategy } from './strategies/target-value-strategy';

export interface ShapeSearchOptions {
  maxShapesToTry?: number;
  maxCandidates?: number;
}

export interface ShapeSearchResult {
  success: boolean;
  firstFitCandidates: Candidate[];
  bestFitCandidate: Candidate | null;
  allCandidates: Candidate[];
  searchMetrics: {
    shapesGenerated: number;
    candidatesProcessed: number;
    searchDuration: number;
    searchPath: string[];
  };
}

export class ShapeSearchManager {
  private strategies: Map<ProjectType, BestFitStrategy>;

  constructor() {
    this.strategies = new Map();
    this.registerStrategies();
  }

  /**
   * Register available search strategies
   */
  private registerStrategies(): void {
    this.strategies.set(ProjectType.MAX_VALUE, new MaxValueStrategy());
    this.strategies.set(ProjectType.TARGET_VALUE, new TargetValueStrategy());
  }

  /**
   * Phase 1: Find initial shapes using first-fit logic
   * Selects 3 predefined shapes for initial exploration
   */
  public findInitialShapes(project: Project, configuration: Configuration): Candidate[] {
    logger.L3.debug(`Finding initial 3 shapes for configuration ${configuration.id}`);

    try {
      // Use the existing buildFirstFitCandidates function
      const candidates = buildFirstFitCandidates(project, configuration);

      logger.L3.debug(`Generated ${candidates.length} first-fit candidates`);

      return candidates;
    } catch (error) {
      logger.error(
        'Failed to generate first-fit candidates:',
        error instanceof Error ? error : undefined
      );
      return [];
    }
  }

  /**
   * Phase 2: Refine to best shape using iterative search
   * Uses strategy pattern to find optimal shape
   */
  public async refineToBestShape(
    firstFitCandidates: Candidate[],
    baseElements: BaseProjectElements,
    configuration: Configuration,
    projectData: SimplifiedProjectData,
    project: Project
  ): Promise<ShapeSearchResult> {
    const startTime = Date.now();

    logger.L3.debug(`Starting best-fit refinement with ${firstFitCandidates.length} candidates`);

    // Detect project type and select strategy
    const projectType = this.detectProjectType(firstFitCandidates, configuration);
    const strategy = this.strategies.get(projectType);

    if (!strategy) {
      return {
        success: false,
        firstFitCandidates,
        bestFitCandidate: null,
        allCandidates: firstFitCandidates,
        searchMetrics: {
          shapesGenerated: 0,
          candidatesProcessed: firstFitCandidates.length,
          searchDuration: Date.now() - startTime,
          searchPath: [],
        },
      };
    }

    // Create search context
    const context: SearchContext = {
      baseElements,
      configuration,
      projectData,
      project,
    };

    // Select best first-fit candidate to start the search
    const bestFirstFit = strategy.selectBestFirstFit(firstFitCandidates, configuration);

    if (!bestFirstFit) {
      return {
        success: false,
        firstFitCandidates,
        bestFitCandidate: null,
        allCandidates: firstFitCandidates,
        searchMetrics: {
          shapesGenerated: 0,
          candidatesProcessed: firstFitCandidates.length,
          searchDuration: Date.now() - startTime,
          searchPath: [],
        },
      };
    }

    // Execute search with selected strategy starting from best first-fit
    const searchResult = await strategy.executeSearch(bestFirstFit, context);

    // Compile results
    const allCandidates = [...firstFitCandidates, ...searchResult.allCandidates];

    return {
      success: searchResult.bestCandidate !== null,
      firstFitCandidates,
      bestFitCandidate: searchResult.bestCandidate,
      allCandidates,
      searchMetrics: {
        shapesGenerated: searchResult.searchDetails.candidatesGenerated,
        candidatesProcessed: searchResult.allCandidates.length + firstFitCandidates.length,
        searchDuration: Date.now() - startTime,
        searchPath: searchResult.searchPath,
      },
    };
  }

  /**
   * Execute complete shape search (first-fit + best-fit)
   */
  public async executeCompleteSearch(
    project: Project,
    configuration: Configuration,
    baseElements: BaseProjectElements,
    projectData: SimplifiedProjectData
  ): Promise<ShapeSearchResult> {
    const startTime = Date.now();

    // Phase 1: First-fit
    const firstFitCandidates = this.findInitialShapes(project, configuration);

    if (firstFitCandidates.length === 0) {
      return {
        success: false,
        firstFitCandidates: [],
        bestFitCandidate: null,
        allCandidates: [],
        searchMetrics: {
          shapesGenerated: 0,
          candidatesProcessed: 0,
          searchDuration: Date.now() - startTime,
          searchPath: [],
        },
      };
    }

    // Phase 2: Best-fit refinement
    const result = await this.refineToBestShape(
      firstFitCandidates,
      baseElements,
      configuration,
      projectData,
      project
    );

    // Update total duration
    result.searchMetrics.searchDuration = Date.now() - startTime;

    return result;
  }

  /**
   * Detect project type based on candidate characteristics
   */
  private detectProjectType(candidates: Candidate[], configuration: Configuration): ProjectType {
    if (candidates.length === 0) {
      return ProjectType.MAX_VALUE;
    }

    const firstCandidate = candidates[0];
    const distributionStrategy = firstCandidate.distributionStrategy;

    // For mixed-targeting or unlimited, use MAX_VALUE strategy
    if (distributionStrategy === 'mixed-targeting' || distributionStrategy === 'unlimited') {
      return ProjectType.MAX_VALUE;
    }

    // Only use TARGET_VALUE for all-targeted scenarios
    if (distributionStrategy === 'all-targeted') {
      // Additional check: do we have actual target values?
      const hasTargets = configuration.bondSeries?.some(
        (bond) =>
          bond.targetInformation?.targetType &&
          bond.targetInformation.principalTargetAmount &&
          bond.targetInformation.principalTargetAmount > 0
      );

      if (hasTargets) {
        return ProjectType.TARGET_VALUE;
      }
    }

    // Default to MAX_VALUE
    return ProjectType.MAX_VALUE;
  }

  /**
   * Get the best candidate from first-fit results
   * Used when not doing best-fit search
   */
  public selectBestFirstFit(candidates: Candidate[]): Candidate | null {
    const firstFitCandidates = candidates.filter((c) => c.createdInPhase === 'first-fit');

    if (firstFitCandidates.length === 0) {
      return null;
    }

    // Select based on highest revenue utilization
    return firstFitCandidates.reduce((best, current) => {
      const currentUtil = current.results?.revenueUtilization || 0;
      const bestUtil = best.results?.revenueUtilization || 0;
      return currentUtil > bestUtil ? current : best;
    });
  }

  /**
   * Evaluate shape search results
   */
  public evaluateSearchResults(result: ShapeSearchResult): {
    efficiency: number;
    improvement: number;
    viableRatio: number;
  } {
    const viableCandidates = result.allCandidates.filter((c) => c.results?.isViable);
    const viableRatio = viableCandidates.length / (result.allCandidates.length || 1);

    // Calculate improvement from first-fit to best-fit
    let improvement = 0;
    if (result.bestFitCandidate && result.firstFitCandidates.length > 0) {
      const bestFirstFit = this.selectBestFirstFit(result.firstFitCandidates);
      if (bestFirstFit) {
        const firstFitValue = bestFirstFit.results?.totalRevenue || 0;
        const bestFitValue = result.bestFitCandidate.results?.totalRevenue || 0;
        improvement = ((bestFitValue - firstFitValue) / (firstFitValue || 1)) * 100;
      }
    }

    // Calculate search efficiency
    const efficiency =
      result.searchMetrics.candidatesProcessed > 0
        ? (viableCandidates.length / result.searchMetrics.candidatesProcessed) * 100
        : 0;

    return {
      efficiency,
      improvement,
      viableRatio,
    };
  }
}
